import { useState, useEffect, useCallback } from 'react';
import { studentAPI } from '../services/api';

export const useStudents = () => {
  const [students, setStudents] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPrevPage: false,
  });
  // Only populated in the 'all' view: counts within the current
  // campus/grade/search scope, regardless of the completion filter — lets
  // the dashboard flag "X of Y haven't completed" even once filtered.
  const [completionMeta, setCompletionMeta] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    sortBy: 'surveyMetadata.completedAt',
    sortOrder: 'desc',
    cluster: '',
    search: '',
    gradeLevel: '',
    campus: '',
    // 'completed' = only students who've completed the survey (the original
    // behavior); 'all' = every student on the roster, completed or not.
    view: 'completed',
    // Only used when view === 'all': '' (any), 'completed', 'not_completed'.
    completion: '',
  });

  // Fetch students with current filters
  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (filters.view === 'all') {
        const params = {
          page: filters.page,
          limit: filters.limit,
          search: filters.search,
          gradeLevel: filters.gradeLevel,
          campus: filters.campus,
          cluster: filters.cluster,
          completion: filters.completion,
        };
        Object.keys(params).forEach((key) => {
          if (!params[key]) delete params[key];
        });

        const response = await studentAPI.getAllWithStatus(params);
        setStudents(response.data);
        setPagination(response.pagination);
        setCompletionMeta(response.meta || null);
      } else {
        const params = { ...filters };
        delete params.view;
        delete params.completion;
        if (!params.cluster) delete params.cluster;
        if (!params.search) delete params.search;
        if (!params.gradeLevel) delete params.gradeLevel;
        if (!params.campus) delete params.campus;

        const response = await studentAPI.getAll(params);
        setStudents(response.data);
        setPagination(response.pagination);
        setCompletionMeta(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch statistics (respects the same filters as the student list)
  const fetchStatistics = useCallback(async () => {
    try {
      const params = {
        cluster: filters.cluster,
        search: filters.search,
        gradeLevel: filters.gradeLevel,
        campus: filters.campus,
      };
      Object.keys(params).forEach((key) => {
        if (!params[key]) delete params[key];
      });

      const response = await studentAPI.getStatistics(params);
      setStatistics(response.data);
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    }
  }, [filters.cluster, filters.search, filters.gradeLevel, filters.campus]);

  // Fetch available clusters
  const fetchClusters = useCallback(async () => {
    try {
      const response = await studentAPI.getClusters();
      setClusters(response.data);
    } catch (err) {
      console.error('Failed to fetch clusters:', err);
    }
  }, []);

  // Fetch available grade levels
  const fetchGradeLevels = useCallback(async () => {
    try {
      const response = await studentAPI.getGradeLevels();
      setGradeLevels(response.data);
    } catch (err) {
      console.error('Failed to fetch grade levels:', err);
    }
  }, []);

  // Fetch available campuses
  const fetchCampuses = useCallback(async () => {
    try {
      const response = await studentAPI.getCampuses();
      setCampuses(response.data);
    } catch (err) {
      console.error('Failed to fetch campuses:', err);
    }
  }, []);

  // Update filters
  const updateFilters = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page !== undefined ? newFilters.page : 1, // Reset to page 1 unless page is explicitly set
    }));
  };

  // Switch between "completed only" and "all students" — starts the new
  // view with no completion filter, so it opens on the unfiltered state
  // that highlights who hasn't completed the survey.
  const setView = (view) => {
    updateFilters({ view, completion: '' });
  };

  // Pagination controls
  const goToPage = (page) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const nextPage = () => {
    if (pagination.hasNextPage) {
      goToPage(pagination.currentPage + 1);
    }
  };

  const prevPage = () => {
    if (pagination.hasPrevPage) {
      goToPage(pagination.currentPage - 1);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Filter option lists only need to be loaded once
  useEffect(() => {
    fetchClusters();
    fetchGradeLevels();
    fetchCampuses();
  }, [fetchClusters, fetchGradeLevels, fetchCampuses]);

  // Statistics should refresh whenever the active filters change
  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  return {
    students,
    statistics,
    clusters,
    gradeLevels,
    campuses,
    loading,
    error,
    pagination,
    completionMeta,
    filters,
    updateFilters,
    setView,
    goToPage,
    nextPage,
    prevPage,
    refetch: fetchStudents,
  };
};
